import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import React, { ReactElement, ReactNode } from 'react'
import { useTranslate } from 'src/localization/Trns'

import { fixedAlertBar } from './FixedAlertBanner.style'

type FixedAlertBannerProps = {
  /** ext-ui severity variant (legacy AlertBarColors: cream→soft, yellow→warning, red→severe) */
  variant: 'soft' | 'warning' | 'severe'
  /** Already-translated bar title (legacy AlertBar messageTitle) */
  title: ReactNode
  /** Already-translated bar body (legacy AlertBar alertMessage) */
  message?: ReactNode
  /** Total alert count; renders a "(+N-1 more)" suffix when greater than 1 (legacy AlertBar messageCount) */
  messageCount?: number
  /** Trailing action element (legacy AlertBar link) */
  link: ReactElement
  /** Fixed-position offset from the top of the viewport, in px (redux pushdown selectors) */
  top: number
  zIndex?: number
  'data-testid': string
}

/**
 * Replacement for the legacy @paywithextend/components AlertBar: an ext-ui
 * AlertBanner pinned to the top of the viewport. The redux pushdown selectors
 * (src/redux/selectors/css.selectors.ts) and the layout components consuming
 * selectFixedElementPushdown assume every open bar is exactly 40px tall and
 * stacked via `top` offsets — FixedAlertBanner.style.ts keeps that geometry.
 */
const FixedAlertBanner: React.FC<FixedAlertBannerProps> = ({
  variant,
  title,
  message,
  messageCount,
  link,
  top,
  zIndex = 1,
  'data-testid': dataTestId,
}): ReactElement => {
  const translate = useTranslate()

  return (
    <AlertBanner
      variant={variant}
      data-testid={dataTestId}
      css={fixedAlertBar}
      style={{ top, zIndex }}
    >
      <div className={'FixedAlertBanner-row'}>
        <p className={'FixedAlertBanner-message'}>
          <span className={'font-weight-medium'}>{title}</span>
          {message !== undefined && message !== null && (
            <>
              {': '}
              {message}
            </>
          )}
        </p>
        {messageCount !== undefined && messageCount > 1 && (
          <span className={'FixedAlertBanner-messageCount'}>
            {translate('(+${messageCount} more)', {
              messageCount: String(messageCount - 1),
            })}
          </span>
        )}
        <span className={'font-weight-medium FixedAlertBanner-link'}>
          {link}
        </span>
      </div>
    </AlertBanner>
  )
}

export default FixedAlertBanner
